/**
 * SummerPrepPanel — surfaces the student's Summer Prep Bridge (previous-grade
 * review) and a live countdown to the Sept 8 first day. Renders inside the
 * School Dashboard. Collapses to a slim banner once school has started.
 */
import React, { useEffect, useState } from 'react';
import { CalendarClock, ChevronDown, ChevronUp, Sparkles, Loader2 } from 'lucide-react';
import api from '../services/api';

const SummerPrepPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('academy_token');
    api.get('/school/summer-prep', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mb-6 border-2 border-zinc-800 px-5 py-4 flex items-center gap-3" data-testid="summer-prep-loading">
        <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
        <span className="text-xs text-zinc-500">Loading your prep bridge…</span>
      </div>
    );
  }
  if (!data?.plan) return null;

  const { countdown, plan } = data;
  const started = countdown?.has_started;

  return (
    <div className="mb-6 border-2 border-orange-500/40 bg-orange-500/[0.04]" data-testid="summer-prep-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid="summer-prep-toggle"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-11 h-11 border-2 border-orange-500 flex items-center justify-center shrink-0">
            <CalendarClock className="w-5 h-5 text-orange-500" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 font-black">
              {started ? 'The Season Is On' : `${countdown.days_until} Days To First Day`}
            </div>
            <p className="text-sm text-white font-bold truncate">
              {started
                ? 'School is in session — daily 3-hour race is live.'
                : `Summer Prep Bridge · Grade ${plan.review_grade} Review`}
            </p>
            <p className="text-[11px] text-zinc-500">School year begins {countdown.start_label}</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />}
      </button>

      {open && (
        <div className="px-5 pb-5" data-testid="summer-prep-body">
          {plan.overview && (
            <div className="bg-black border-l-4 border-emerald-500 px-4 py-3 mb-4">
              <p className="text-sm text-zinc-200 leading-relaxed">{plan.overview}</p>
            </div>
          )}

          {Array.isArray(plan.weekly_plan) && plan.weekly_plan.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-5">
              {plan.weekly_plan.map((w) => (
                <div key={w.week} className="border-2 border-zinc-800 p-3" data-testid={`prep-week-${w.week}`}>
                  <div className="text-[10px] font-black uppercase tracking-widest text-orange-500">Week {w.week}</div>
                  <div className="text-xs font-bold text-white mt-1 leading-tight">{w.theme}</div>
                  <p className="text-[11px] text-zinc-500 mt-1 leading-snug">{w.focus}</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            {(plan.subjects || []).map((s, i) => (
              <div key={i} className="border-2 border-zinc-800 p-4" data-testid={`prep-subject-${i}`}>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-orange-500" />
                  <h4 className="text-sm font-black uppercase tracking-tight text-white">{s.subject}</h4>
                </div>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{s.focus}</p>
                {Array.isArray(s.standards) && s.standards.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.standards.filter((st) => st.code).slice(0, 4).map((st, j) => (
                      <span key={j} className="text-[9px] font-mono border border-zinc-700 text-zinc-400 px-1.5 py-0.5" title={st.text}>{st.code}</span>
                    ))}
                  </div>
                )}
                <ul className="mt-3 space-y-1.5">
                  {(s.activities || []).map((a, j) => (
                    <li key={j} className="text-[12px] text-zinc-300 leading-snug flex gap-2">
                      <span className="text-orange-500 font-black shrink-0">{j + 1}.</span>{a}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SummerPrepPanel;
